import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, User } from '../../contexts/AuthContext';
import { getPublicSetting, getOAuthAuthorizeUrl } from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import './Auth.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// Ordered list of modules → their paths (matches sidebar order).
// The first module the user has access to becomes their landing page.
const MODULE_PATHS: { key: string; path: string }[] = [
    { key: 'dashboard',    path: '/dashboard' },
    { key: 'content',      path: '/content' },
    { key: 'landing_pages', path: '/landing-pages' },
    { key: 'quizzes',      path: '/quizzes' },
    { key: 'scripts',      path: '/scripts' },
    { key: 'brand',        path: '/brand' },
    { key: 'websites',     path: '/websites' },
    { key: 'websites_v2',  path: '/websites-v2' },
    { key: 'leads_crm',    path: '/leads' },
    { key: 'teams',        path: '/teams' },
];

function getFirstAccessiblePath(user: User): string {
    // Admins and unrestricted users always land on dashboard
    if (user.role === 'admin' || !user.allowed_modules) return '/dashboard';
    const match = MODULE_PATHS.find(m => user.allowed_modules!.includes(m.key));
    return match ? match.path : '/dashboard';
}

const LoginPage: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [oauthLoading, setOauthLoading] = useState<string | null>(null);
    const [registrationEnabled, setRegistrationEnabled] = useState(false);

    const { login } = useAuth();

    const handleOAuth = async (provider: string) => {
        setOauthLoading(provider);
        setError('');
        try {
            const result = await getOAuthAuthorizeUrl(provider);
            if (!result.configured || !result.authorization_url) {
                setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} SSO is not configured on this server.`);
                return;
            }
            window.location.href = result.authorization_url;
        } catch {
            setError(`Failed to initiate ${provider} sign-in. Please try again.`);
        } finally {
            setOauthLoading(null);
        }
    };

    useEffect(() => {
        const checkRegistration = async () => {
            try {
                const setting = await getPublicSetting('registration_enabled');
                setRegistrationEnabled(setting.value.toLowerCase() === 'true');
            } catch {
                setRegistrationEnabled(false);
            }
        };
        checkRegistration();
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const sessionExpired = new URLSearchParams(location.search).get('session_expired') === '1';

    // If the user was redirected here from a protected page, send them back
    // there after login.  Otherwise pick their first accessible module.
    const from = (location.state as any)?.from?.pathname;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const loggedInUser = await login(username, password);
            // If there's a saved destination honour it; otherwise go to the
            // first module this user is allowed to access.
            const destination = from || getFirstAccessiblePath(loggedInUser);
            navigate(destination, { replace: true });
        } catch (err: any) {
            console.error('Login error:', err);
            setError(getApiError(err, 'Failed to login. Please check your credentials.'));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-page">
            <GlassCard variant="strong" padding="lg" className="auth-container">
                <div className="auth-header">
                    <h1>AiMe</h1>
                    <h2>Sign In</h2>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {sessionExpired && !error && (
                        <div className="auth-error" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.4)', color: 'var(--warning, #b45309)' }}>
                            Your session expired — please sign in again.
                        </div>
                    )}
                    {error && (
                        <div className="auth-error">
                            {error}
                        </div>
                    )}

                    <GlassInput
                        label="Username or Email"
                        type="text"
                        value={username}
                        onChange={setUsername}
                        placeholder="Enter your username or email"
                        autoFocus
                        disabled={isLoading}
                    />

                    <GlassInput
                        label="Password"
                        type="password"
                        value={password}
                        onChange={setPassword}
                        placeholder="Enter your password"
                        disabled={isLoading}
                    />

                    <div style={{ textAlign: 'right', marginTop: '-8px' }}>
                        <Link
                            to="/reset-password"
                            style={{
                                color: 'var(--primary-500)',
                                fontSize: 'var(--text-sm)',
                                textDecoration: 'none',
                                fontWeight: 'var(--font-medium)' as any,
                            }}
                        >
                            Forgot password?
                        </Link>
                    </div>

                    <GlassButton
                        type="submit"
                        variant="primary"
                        size="lg"
                        loading={isLoading}
                        disabled={isLoading}
                        className="auth-submit-btn"
                    >
                        Sign In
                    </GlassButton>
                </form>

                <div className="auth-sso-divider">
                    <span>or continue with</span>
                </div>

                <div className="auth-sso-buttons">
                    <GlassButton
                        type="button"
                        variant="secondary"
                        size="md"
                        loading={oauthLoading === 'google'}
                        disabled={isLoading || oauthLoading !== null}
                        onClick={() => handleOAuth('google')}
                        className="auth-sso-btn"
                    >
                        Google
                    </GlassButton>
                    <GlassButton
                        type="button"
                        variant="secondary"
                        size="md"
                        loading={oauthLoading === 'microsoft'}
                        disabled={isLoading || oauthLoading !== null}
                        onClick={() => handleOAuth('microsoft')}
                        className="auth-sso-btn"
                    >
                        Microsoft
                    </GlassButton>
                </div>

                {registrationEnabled && (
                    <div className="auth-footer">
                        <p>
                            Don't have an account?{' '}
                            <Link to="/register">Create one</Link>
                        </p>
                    </div>
                )}

                <p style={{ textAlign: 'center', marginTop: '12px' }}>
                    <Link to="/support" style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Need help?</Link>
                </p>
            </GlassCard>
        </div>
    );
};

export default LoginPage;
